const DEFAULT_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

type ActivityEventName = (typeof DEFAULT_ACTIVITY_EVENTS)[number];

export type IdleDisconnectController = {
  setConnected: (connected: boolean) => void;
  setPaused: (paused: boolean) => void;
  setTimeoutMs: (timeoutMs: number) => void;
  getDebugState: () => IdleDisconnectDebugState;
  destroy: () => void;
};

export type IdleDisconnectControllerOptions = {
  timeoutMs: number;
  onTimeout: () => void;
  onActivity?: () => void;
  activityEvents?: readonly ActivityEventName[];
};

export type IdleDisconnectDebugState = {
  connected: boolean;
  paused: boolean;
  active: boolean;
  timerRunning: boolean;
  timeoutMs: number;
  deadlineAtMs: number | null;
  msRemaining: number | null;
  lastActivityAtMs: number | null;
};

export function createIdleDisconnectController(
  options: IdleDisconnectControllerOptions
): IdleDisconnectController {
  let timeoutMs = Math.max(0, options.timeoutMs);
  const activityEvents = options.activityEvents ?? DEFAULT_ACTIVITY_EVENTS;

  let connected = false;
  let paused = false;
  let timeoutId: number | null = null;
  let deadlineAtMs: number | null = null;
  let lastActivityAtMs: number | null = null;
  let destroyed = false;

  const isActive = (): boolean => connected && !paused;

  const clearTimer = (): void => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
    deadlineAtMs = null;
  };

  const scheduleTimer = (): void => {
    clearTimer();
    if (!isActive()) {
      return;
    }

    deadlineAtMs = Date.now() + timeoutMs;
    timeoutId = window.setTimeout(() => {
      timeoutId = null;
      deadlineAtMs = null;
      if (!isActive()) {
        return;
      }
      options.onTimeout();
    }, timeoutMs);
  };

  const handleActivity = (): void => {
    lastActivityAtMs = Date.now();
    options.onActivity?.();
    if (!isActive()) {
      return;
    }
    scheduleTimer();
  };

  activityEvents.forEach((eventName) => {
    window.addEventListener(eventName, handleActivity, { passive: true });
  });

  return {
    setConnected(nextConnected: boolean): void {
      if (destroyed) {
        return;
      }
      if (connected === nextConnected) {
        return;
      }
      connected = nextConnected;
      if (isActive()) {
        scheduleTimer();
      } else {
        clearTimer();
      }
    },
    setPaused(nextPaused: boolean): void {
      if (destroyed) {
        return;
      }
      if (paused === nextPaused) {
        return;
      }
      paused = nextPaused;
      if (isActive()) {
        scheduleTimer();
      } else {
        clearTimer();
      }
    },
    setTimeoutMs(nextTimeoutMs: number): void {
      if (destroyed) {
        return;
      }
      const normalized = Math.max(0, nextTimeoutMs);
      if (timeoutMs === normalized) {
        return;
      }
      timeoutMs = normalized;
      if (isActive()) {
        scheduleTimer();
      } else {
        clearTimer();
      }
    },
    getDebugState(): IdleDisconnectDebugState {
      const now = Date.now();
      const msRemaining =
        deadlineAtMs === null ? null : Math.max(0, deadlineAtMs - now);
      return {
        connected,
        paused,
        active: isActive(),
        timerRunning: timeoutId !== null,
        timeoutMs,
        deadlineAtMs,
        msRemaining,
        lastActivityAtMs,
      };
    },
    destroy(): void {
      if (destroyed) {
        return;
      }
      destroyed = true;
      clearTimer();
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    },
  };
}
